import _ from 'underscore';
import { MetricCategories, Metrics, getAllMetrics } from './metrics/index';
import { segmentCategories, getAllSegments } from './segments/index';
import {
  Permissions, ICustomColumnsSharedConfig, UsedMetricsAndSegments, IMetricDefinition,
} from '../types';

/**
 * @typedef {Object} SegmentDefinition
 * @property {string} segment internal segment name (for OData)
 * @property {string} titleKey i18n key for segment name (for UI)
 */

/**
 * @typedef {Object} SegmentCategory
 * @property {string} category internal segment category name (for OData)
 * @property {string} titleKey i18n key for segment category name (for UI)
 * @property {SegmentDefinition[]} segments list of valid segment definitions
 */

/**
 * @typedef {Object} MetricDefinition
 * @property {string} metric internal metric name (for OData)
 * @property {string} titleKey i18n key for metric name (for UI)
 */

/**
 * @typedef {Object} MetricCategory
 * @property {string} titleKey i18n key for metric category name (for UI)
 * @property {MetricDefinition[]} metrics list of valid metric definitions
 */

/**
 * Configuration shared between custom-columns-related constructors/components.
 *
 * @typedef {Object} CustomColumnsSharedConfig
 * @property {boolean} enabled whether the custom columns feature should be enabled
 * @property {MetricCategory[]} [metricCategories=[]] whitelist of valid metrics
 * @property {SegmentCategory[]} [segmentCategories=[]] whitelist of valid segments
 */

/**
 * @type {ICustomColumnsSharedConfig}
 */
export const EMPTY: ICustomColumnsSharedConfig = {
  enabled: false,
  metricCategories: [],
  segmentCategories: [],
};

/**
 * Sane, minimal defaults for custom columns to be enabled.
 * These metrics and segments are used in ALL grids.
 * Some grids may include additional metrics/segments.
 * @type {ICustomColumnsSharedConfig}
 */
export const DEFAULTS: ICustomColumnsSharedConfig = {
  enabled: true,
  metricCategories: MetricCategories(),
  segmentCategories,
};

const getConversionMetrics = (
  permissions: Permissions = { dynamic: {} },
  combine?: boolean,
): IMetricDefinition[] | undefined => {
  const {
    dynamic: {
      PartialConversions: IsPartialConversionsEnabled,
      ColumnConversion: IsColumnConversionEnabled,
    } = {},
  } = permissions;

  if (!IsColumnConversionEnabled) return undefined;

  const newMetrics = [
    Metrics.PartialConversionCPA,
    Metrics.ConversionsCredit,
    Metrics.PartialConversionRate,
    Metrics.AdvertiserReportedRevenue,
    Metrics.RevenueOnAdSpend,
  ];
  const oldMetrics = [
    Metrics.CPA,
    Metrics.Conversions,
    Metrics.ConversionRate,
    Metrics.AdvertiserReportedRevenue,
    Metrics.RevenueOnAdSpend,
  ];
  const selectedMetrics = IsPartialConversionsEnabled ? newMetrics : oldMetrics;
  let addedMetrics: IMetricDefinition[] = [];
  if (combine) {
    addedMetrics = IsPartialConversionsEnabled ? oldMetrics : newMetrics;
  }

  const convMetrics = [...selectedMetrics, ...addedMetrics];

  return convMetrics;
};

export const adGroupMetricCategoriesFactory = (
  permissions: Permissions,
  combine: boolean = false,
): ICustomColumnsSharedConfig => {
  const convMetrics = getConversionMetrics(permissions, combine);

  return {
    metricCategories: MetricCategories([
      Metrics.Clicks,
      Metrics.Impressions,
      Metrics.CTR,
      Metrics.AverageCPC,
      Metrics.AverageCPM,
      Metrics.Spend,
      Metrics.TotalSpend,
    ], convMetrics),
    segmentCategories,
  };
};

export const adsMetricCategoriesFactory = (
  permissions: Permissions,
  combine: boolean = false,
): ICustomColumnsSharedConfig => {
  const convMetrics = getConversionMetrics(permissions, combine);

  return {
    metricCategories: MetricCategories([
      Metrics.Clicks,
      Metrics.Impressions,
      Metrics.CTR,
      Metrics.AverageCPC,
      Metrics.Spend,
    ], convMetrics),
    segmentCategories,
  };
};

export const campaignMetricCategoriesFactory = (
  permissions: Permissions,
  combine: boolean = false,
): ICustomColumnsSharedConfig => {
  const convMetrics = getConversionMetrics(permissions, combine);

  return {
    metricCategories: MetricCategories([
      Metrics.Clicks,
      Metrics.Impressions,
      Metrics.CTR,
      Metrics.AverageCPC,
      Metrics.AverageCPM,
      Metrics.Spend,
      Metrics.TotalSpend,
    ], convMetrics),
    segmentCategories,
  };
};

export const keywordMetricCategoriesFactory = (
  permissions: Permissions,
  combine: boolean = false,
): ICustomColumnsSharedConfig => {
  const convMetrics = getConversionMetrics(permissions, combine);

  return {
    metricCategories: MetricCategories([
      Metrics.Clicks,
      Metrics.Impressions,
      Metrics.CTR,
      Metrics.AverageCPC,
      Metrics.AverageCPM,
      Metrics.Spend,
    ], convMetrics),
    segmentCategories,
  };
};

export const searchTermMetricCategoriesFactory = (
  permissions: Permissions,
  combine: boolean = false,
): ICustomColumnsSharedConfig => {
  const convMetrics = getConversionMetrics(permissions, combine);

  return {
    enabled: true,
    metricCategories: MetricCategories(undefined, convMetrics),
    segmentCategories,
  };
};

export const accountMetricCategoriesFactory = (
  permissions: Permissions,
  combine: boolean = false,
): ICustomColumnsSharedConfig => {
  const convMetrics = getConversionMetrics(permissions, combine);

  return {
    metricCategories: MetricCategories([
      Metrics.Clicks,
      Metrics.Impressions,
      Metrics.CTR,
      Metrics.AverageCPC,
      Metrics.AverageCPM,
      Metrics.Spend,
    ], convMetrics),
    segmentCategories,
  };
};

export const adExtensionMetricCategories: ICustomColumnsSharedConfig = {
  metricCategories: MetricCategories([
    Metrics.Clicks,
    Metrics.Impressions,
    Metrics.CTR,
    Metrics.AverageCPC,
    Metrics.AverageCPM,
    Metrics.Spend,
  ]),
  segmentCategories,
};

function getUsedMetricsAndSegments(expression: string[]): UsedMetricsAndSegments {
  let usedMetrics: string[] = [];
  let usedSegments: string[] = [];
  const metrics = _.filter(
    expression,
    (clause) => clause.indexOf('PerformanceMetrics') === 0
      || clause.indexOf('PhonePerformanceMetrics') === 0,
  );
  // @ts-ignore
  const regMetric = /(?<metric>(PerformanceMetrics|PhonePerformanceMetrics)[^[]*)(\[[^:]*:(?<segments>.*)\])?/;

  _.each(metrics, (metric) => {
    const {
      groups: { metric: matchedMetric, segments },
    } = metric.match(regMetric) as RegExpMatchArray & { groups: { metric: string; segments: string } };
    usedMetrics = usedMetrics.concat(matchedMetric);
    if (segments) {
      usedSegments = usedSegments.concat(segments.split(','));
    }
  });
  return {
    usedMetrics: _.uniq(usedMetrics),
    usedSegments: _.uniq(usedSegments),
  };
}

export function isValidForFeature({
  schemaName,
  expression,
  permissions = {},
}: {
  schemaName: string;
  expression: string[];
  permissions?: Permissions;
}): boolean {
  let metricCategories;
  let segCategories;

  switch (schemaName) {
    case 'Account':
      ({ metricCategories, segmentCategories: segCategories } = accountMetricCategoriesFactory(permissions, /* combine */ true));
      break;
    case 'Campaign':
      ({ metricCategories, segmentCategories: segCategories } = campaignMetricCategoriesFactory(permissions, /* combine */ true));
      break;
    case 'AdGroup':
      ({ metricCategories, segmentCategories: segCategories } = adGroupMetricCategoriesFactory(permissions, true));
      break;
    case 'Ad':
      ({ metricCategories, segmentCategories: segCategories } = adsMetricCategoriesFactory(permissions, true));
      break;
    case 'Keyword':
      ({ metricCategories, segmentCategories: segCategories } = keywordMetricCategoriesFactory(permissions, true));
      break;
    case 'SearchTerm':
      ({ metricCategories, segmentCategories: segCategories } = searchTermMetricCategoriesFactory(permissions, true));
      break;
    case 'AdExtensionAssociation':
      ({ metricCategories, segmentCategories: segCategories } = adsMetricCategoriesFactory(permissions, true));
      break;
    default:
      return false;
  }

  const supportedMetrics = getAllMetrics(metricCategories);
  const supportedSegments = getAllSegments(segCategories);

  const { usedMetrics, usedSegments } = getUsedMetricsAndSegments(expression);

  if (!_.chain(usedMetrics).difference(supportedMetrics).isEmpty().value()) {
    return false;
  }

  if (!_.chain(usedSegments).difference(supportedSegments).isEmpty().value()) {
    return false;
  }

  return true;
}
